// Wording and small pieces shared by the inbox pages.
import { Pill } from '../../components/ui/index.jsx';
import VerifiedBadge from '../../components/ui/VerifiedSeal.jsx';

export const VERDICT = {
  scam: { tone: 'bad', label: 'Scam' },
  suspicious: { tone: 'bad', label: 'Likely scam' },
  cannot_verify: { tone: 'warn', label: 'Not confirmed' },
  likely_genuine: { tone: 'good', label: 'Looks genuine' },
  verified: { tone: 'good', label: 'Verified' },
};

export const DECISION = {
  official: { tone: 'info', label: 'You said: official', short: 'Official' },
  scam: { tone: 'bad', label: 'You confirmed: scam', short: 'Confirmed scam' },
  phishing: { tone: 'bad', label: 'You confirmed: phishing', short: 'Phishing' },
  not_related: { tone: 'neutral', label: 'You said: not about us', short: 'Not about us' },
  unsure: { tone: 'neutral', label: 'You said: not sure', short: 'Not sure' },
};

/** What each verdict means, said once, the same everywhere. */
export const VERDICT_WHY = {
  scam: 'We found a definite sign it isn’t from you.',
  suspicious: 'It says it’s from you, but it wasn’t sent by you and it has the marks of a scam. Nothing is reported until you confirm.',
  cannot_verify: 'Nothing proves it’s from you, and only you can say what it is.',
  likely_genuine: 'Sent from your domain and everything in it is yours. It was forwarded in a way that hides where it came from, so it isn’t proof.',
  verified: 'It really came from you, and everything in it is yours.',
};

export const STATUS_WAITING = { received: 'Checking…', parsed: 'Checking…', failed: 'Couldn’t read it', rejected: 'Not checked (test mode)' };

/** The verdict: the seal for verified (one look for "verified" everywhere), pills otherwise. */
export function VerdictPill({ verdict, status }) {
  if (!verdict) return <Pill>{STATUS_WAITING[status] || 'Checking…'}</Pill>;
  if (verdict === 'verified') return <VerifiedBadge>Verified</VerifiedBadge>;
  const v = VERDICT[verdict];
  return <Pill tone={v.tone}>{v.label}</Pill>;
}

// A decision overrides our automatic verdict: these become the verdict, marked "Manual"
export const DECIDED_VERDICT = {
  scam: { tone: 'bad', label: 'Scam' },
  phishing: { tone: 'bad', label: 'Phishing' },
  official: { tone: 'good', label: 'Official' },
  not_related: { tone: 'neutral', label: 'Not about us' },
};

/**
 * The verdict that stands: the company's decision when there is one (marked Manual),
 * otherwise our automatic check. `showCheck` adds what our check said underneath.
 */
export function FinalVerdict({ verdict, decision, status, showCheck = false }) {
  const d = DECIDED_VERDICT[decision];
  if (!d) return <VerdictPill verdict={verdict} status={status} />;
  return (
    <span className="final-verdict">
      <span className="title-with-pill">
        {decision === 'official' ? <VerifiedBadge>Official</VerifiedBadge> : <Pill tone={d.tone}>{d.label}</Pill>}
        <span className="manual-tag" title="Decided by your team. It replaces our automatic check.">Manual</span>
      </span>
      {/* Only worth saying when the decision changed it */}
      {showCheck && verdict && !(verdict === 'scam' && decision === 'scam') && !(['verified', 'likely_genuine'].includes(verdict) && decision === 'official') && <span className="muted small">Our check said: {verdict === 'verified' ? 'Verified' : VERDICT[verdict]?.label}</span>}
    </span>
  );
}

export const ADDED_HOW = { file: 'uploaded email file', source: 'pasted email source', text: 'pasted email text', approach: 'described call or message' };

export const FORWARD_TYPE = {
  attached: 'Forwarded as an attachment',
  redirect: 'Redirected (original headers kept)',
  inline: 'Forwarded as text',
  direct: 'Sent straight to the check address',
};

export const AUTH_SOURCE = {
  dkim_reverified: 'We checked its digital signature ourselves.',
  provider: 'From the check the recipient’s email provider made when it arrived.',
  smtp: 'Our mail server checked it when it arrived.',
  none: 'There was nothing we could check.',
};

export const KIND = {
  sender: 'Sender', reply_to: 'Replies go to', link: 'Link', email: 'Email address', phone: 'Phone number', bank: 'Bank account',
};

export const MATCH = {
  theirs: { tone: 'info', label: 'Yours', why: 'It’s in your records.' },
  listed: { tone: 'info', label: 'Yours, not proven', why: 'It’s in your records, but you haven’t proved the domain is yours yet — do that on the Company page.' },
  lookalike: { tone: 'bad', label: 'Lookalike', why: 'Made to look like one of yours.' },
  known_scam: { tone: 'bad', label: 'Known scam', why: 'Already confirmed as a scam, here or at another event.' },
  neutral: { tone: 'neutral', label: 'Common service', why: 'Something everyone uses, like Google Maps or LinkedIn. Proves nothing either way.' },
  tracking: { tone: 'neutral', label: 'Email service link', why: 'A newsletter tool’s click-tracking link. Proves nothing either way.' },
  unknown: { tone: 'warn', label: 'Not yours', why: 'Not in your records. It may be harmless, but it isn’t yours.' },
};

export const SEVERITY_TONE = { scam: 'danger', caution: 'warn', good: 'success', info: 'info' };

/** Show a web address without making it clickable: hxxps://bad[.]com/path */
export function defang(url) {
  const s = String(url || '').replace(/^http/i, 'hxxp');
  return s.replace(/^(hxxps?:\/\/)?([^/?#]+)/i, (_, scheme = '', host) => `${scheme}${host.replace(/\./g, '[.]')}`);
}

/** Body text with every web address defanged, so nothing in it can be clicked. */
export function defangText(text) {
  return String(text || '').replace(/\bhttps?:\/\/[^\s<>"')\]]+/gi, (u) => defang(u));
}

export const fmtBytes = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// ---- Plain words, shared by the Messages list and the full message page ----

// The verdict that stands (the company's decision, else our check), in everyday words
export const PLAIN = {
  scam: { label: 'Scam', tone: 'bad', headline: 'It isn’t from you' },
  phishing: { label: 'Phishing', tone: 'bad', headline: 'It isn’t from you, and it tries to get people’s logins' },
  suspicious: { label: 'Probably a scam', tone: 'warn', headline: 'It isn’t from you, and it looks like a scam' },
  cannot_verify: { label: 'Not confirmed', tone: 'warn', headline: 'We couldn’t confirm it’s from you' },
  likely_genuine: { label: 'Looks genuine', tone: 'good', headline: 'Everything in it points to you' },
  verified: { label: 'Really from you', tone: 'good', headline: 'This really is your email' },
  official: { label: 'Really from you', tone: 'good', headline: 'You said this is yours' },
  not_related: { label: 'Not about you', tone: 'neutral', headline: 'You said it’s nothing to do with you' },
};
export const finalOf = (m) => (['scam', 'phishing', 'official', 'not_related'].includes(m.decision) ? m.decision : m.verdict);
export const plainOf = (m) => PLAIN[finalOf(m)] || { label: STATUS_WAITING[m.status] || 'Checking…', tone: 'neutral', headline: 'We’re still checking it' };
/**
 * Which answer card stands for this message now: the company's own answer, else what our
 * check found (a scam, or proven to be theirs). Null when nothing is settled yet.
 */
export function currentAnswer(m) {
  if (m.decision && m.decision !== 'unsure') return { key: m.decision, by: 'you' };
  if (m.verdict === 'scam') return { key: 'scam', by: 'us' };
  if (['verified', 'likely_genuine'].includes(m.verdict)) return { key: 'official', by: 'us' };
  return null;
}
export const ANSWER_TONE = { scam: 'bad', phishing: 'bad', official: 'good', ours: 'good', not_related: 'neutral', watch: 'neutral' };

/** One answer card: neutral until it's the answer that stands, then coloured and labelled. */
export function AnswerCard({ k, title, what, current, disabled, onClick }) {
  const on = current?.key === k;
  return (
    <button type="button" className={`msg-answer tone-${ANSWER_TONE[k] || 'neutral'}${on ? ' is-current' : ''}`}
      aria-pressed={on} disabled={disabled} onClick={onClick}>
      {on && <em className="msg-answer-tag">{current.by === 'you' ? 'Your answer' : 'What we found'}</em>}
      <strong>{title}</strong><span>{what}</span>
    </button>
  );
}

export const needsDecision = (m) => (!m.decision || m.decision === 'unsure') && ['suspicious', 'cannot_verify'].includes(m.verdict);

export function whoOf(m) {
  if (m.added_by || m.added) return `Added by ${m.added_by || m.added?.by || 'your team'}`;
  if (m.from_verify_page || m.verify_page) return 'Checked on your check page';
  if (m.honeypot) return 'Sent to a contact you planted';
  if (m.forwarder_domain) return `Sent to us by someone at @${m.forwarder_domain}`;
  return 'Sent to your check address';
}

/** What we've already said to the person who asked, in plain words. */
export function toldText(reply) {
  if (!reply) return null;
  if (reply.outcome && reply.outcome.status === 'sent') return 'We’ve passed your answer on to them.';
  if (reply.status === 'sent' && reply.kind === 'holding') return 'Not to act on it until you decide. When you answer, we pass it on.';
  if (reply.status === 'sent') return 'What we found, straight away.';
  return null;
}

